import { type UseInfiniteQueryOptions } from "@tanstack/react-query";
import { type AxiosError } from "axios";

import {
  type GetPlacementsResponse,
  PlacementApplicationMethod,
  PlacementApplicationStatus,
} from "../types/fetchPlacements.schema";
import {
  type FetchPlacementsParams,
  useFetchPaginatedPlacements,
} from "./useFetchPaginatedPlacements";

/**
 * Deadline in days for workers to book an interview for easy scheduling placements.
 * After this deadline, the placement will no longer be shown as urgent.
 */
export const EASY_SCHEDULING_BOOKING_DEADLINE_DAYS = 10;

/**
 * Default page size for easy scheduling placements.
 * Larger than default to enable client-side counting without additional requests.
 */
const DEFAULT_PAGE_SIZE = 200;

interface UseGetEasySchedulingPlacementsParams extends Omit<FetchPlacementsParams, "filter"> {
  additionalFilters?: FetchPlacementsParams["filter"];
}

/**
 * Fetch placements with easy scheduling enabled.
 *
 * Easy scheduling placements are those where:
 * - Workplace has interview settings with scheduleInterviews: false (non-instant-book)
 * - Workplace has at least one availability time slot configured
 * - Feature flag enableHiringNonInstantBookEasyScheduling is enabled for the workplace
 * - Worker has been accepted but hasn't booked an interview yet
 *
 * Note: Uses a larger page size (200) to enable efficient client-side counting.
 * For badge counts, use data?.pages[0]?.data.length ?? 0 from the first page.
 */
export function useGetEasySchedulingPlacements(
  params: UseGetEasySchedulingPlacementsParams,
  options: UseInfiniteQueryOptions<GetPlacementsResponse, AxiosError> = {}
) {
  const { workerId, placementCandidateId, additionalFilters, shouldLogEvent, sort, pageSize } =
    params;

  return useFetchPaginatedPlacements(
    {
      workerId,
      placementCandidateId,
      shouldLogEvent,
      sort,
      pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
      filter: {
        ...additionalFilters,
        applicationMethod: PlacementApplicationMethod.EASY_SCHEDULING,
        "applications.status": PlacementApplicationStatus.ACCEPTED,
      },
    },
    options
  );
}
